<template>
    <div class="map">
        <div class="header">
            <el-row>
                <el-col>
                    <div class="page-title bold fnt-lg dark text-left">
                        Карта монтажа
                    </div>
                </el-col>
            </el-row>
        </div>
        <div class="content">
            <el-row>
                <el-col :span="4" style="width: 14%">
                    <el-row class="mt10">
                        <el-col>
                            <el-button
                                icon="el-icon-message"
                                @click="handleInformButtonClick"
                                type="warning"
                                class="send"
                                :disabled="!isCanEdit"
                            >
                                Сообщить монтажникам
                            </el-button>
                        </el-col>
                    </el-row>
                    <el-row class="mt10">
                        <el-col>
                            <div class="filter-title">
                                Поиск
                            </div>
                            <el-form label-position="top" class="filter-form">
                                <el-form-item label="Дата монтажа">
                                    <el-date-picker
                                        v-model="selectedDay"
                                        @change="changeDate()"
                                        type="date"
                                        placeholder="дд.мм.гггг"
                                        format="dd.MM.yyyy"
                                        value-format="dd.MM.yyyy"
                                        :picker-options="pickerOptions"
                                        :clearable="false"
                                        size="mini"
                                        class="w100"
                                    />
                                </el-form-item>
                            </el-form>
                            <el-form label-position="top" class="filter-form">
                                <el-form-item label="Фильтр по офисам" />
                                <template v-for="(office) in availableOffices">
                                    <div
                                        :key="office.office_id"
                                    >
                                        <el-checkbox v-model="offices[office.office_id]" @change="selectOffice(office.office_id)">
                                            {{ office.name }}
                                        </el-checkbox>
                                    </div>
                                </template>
                            </el-form>
                            <el-form label-position="top" class="filter-form">
                                <el-form-item>
                                    <span style="font-size: 16px">Найдено {{ deals.length }} монтажей</span>
                                </el-form-item>
                            </el-form>
                            <el-form label-position="top" class="filter-form">
                                <hr>
                                <el-form-item label="Максимальное кол-во доставок в день">
                                    <el-input v-model="deliveryLimitPerDay" autocomplete="off" size="mini" />
                                </el-form-item>
                                <el-form-item label="Максимальное кол-во монтажей в день">
                                    <el-input v-model="installLimitPerDay" autocomplete="off" size="mini" />
                                </el-form-item>
                                <el-button
                                    @click="handleUpdateSettingsButtonClick"
                                    type="warning"
                                    :disabled="!isCanEdit"
                                >
                                    Сохранить
                                </el-button>
                            </el-form>
                        </el-col>
                    </el-row>
                </el-col>
                <el-col :span="20" id="map" style="min-height: 800px" />
            </el-row>
        </div>
    </div>
</template>

<script>
/*global ymaps*/
import '@/assets/styles/map.scss';
import moment from 'moment';
import localReferences from '@/helpers/localReferences';
import { pickerOptions } from '@/common/pickerOptions';

const garbageRemoval = [localReferences.garbageRemovals.actionPlastic, localReferences.garbageRemovals.paid];

export default {
    name: 'InstallationMap',
    data() {
        return {
            loading: false,
            pickerOptions,
            selectedDay: moment().add(1, 'days').format('DD.MM.YYYY'),
            officeIds: '',
            isMapReady: false,
            mapObject: {},
            collection: [],
            installLimitPerDay: 0,
            deliveryLimitPerDay: 0,
            offices: []
        };
    },
    computed: {
        deals() {
            return this.$store.state.map.deals;
        },
        availableOffices() {
            return this.$store.state.map.availableOffices;
        },
        installers() {
            return this.$store.state.map.installers.filter(employer => !employer.date_fired);
        },
        isCanEdit() {
            return this.$store.getters.isCanEditInstallationMap;
        },
        settings() {
            return this.$store.state.auth.settings;
        }
    },
    methods: {
        getMapScript() {
            const mapScript = document.createElement('script');
            const options = { apiKey: this.$store.getters.yandexApiKey, lang: 'ru_RU', version: '2.1' };
            const mapLink = `https://api-maps.yandex.ru/${options.version}/?lang=${options.lang}${options.apiKey && `&apikey=${options.apiKey}`}`;
            mapScript.setAttribute('src', mapLink);
            mapScript.setAttribute('async', '');
            mapScript.setAttribute('defer', '');
            document.body.appendChild(mapScript);
            mapScript.onload = () => {
                this.initMap();
            };
        },
        initMap() {
            ymaps.ready(() => {
                this.mapObject = new ymaps.Map('map', {
                    center: localReferences.mapCenterCoordinates,
                    behaviors: ['default', 'scrollZoom'],
                    zoom: 10,
                    balloonPane: 'movableOuters',
                    controls: []
                });
                this.mapObject.events.add('balloonopen', () => {
                    this.mapObject.events.add('click', event => {
                        if (event.get('target') === this.mapObject) {
                            this.mapObject.balloon.close();
                        }
                    });
                });
                this.mapObject.container.fitToViewport();
                this.isMapReady = true;
                this.placeMarks();
            });
        },
        async placeMarks() {
            const dealsWithoutCoordinates = this.deals.filter(deal => deal.coordinates === null).filter(deal => deal.address !== null).filter(deal => deal.address !== '');
            for (const deal of dealsWithoutCoordinates) {
                await this.$store.dispatch('map/getCoordinatesByAddress', encodeURIComponent(deal.address)).then(
                    res => {
                        const coords = res || localReferences.mapCenterCoordinates;
                        this.$store.dispatch('map/updateCoordinates', { dealId: deal.id, coords: coords.join(' ') });
                    }).catch(
                    error => {
                        console.log(error);
                    });
            }
            this.mapObject.geoObjects.removeAll();
            this.collection = new ymaps.GeoObjectCollection();
            this.deals.forEach(deal => {
                this.collection.add(this.createMapMark(deal));
            });
            this.mapObject.geoObjects.add(this.collection);
            const mapBounds = this.mapObject.geoObjects.getBounds();
            if (mapBounds) {
                this.mapObject.setBounds(this.mapObject.geoObjects.getBounds());
                //Проверка автоматически выбранного масштаба карты
                if (this.mapObject.getZoom() > 12) {
                    this.mapObject.setZoom(12);
                }
            }
        },
        createBalloonHint(deal) {
            let hintContent = `Монтаж ${deal.installation_date}<br>Адрес: ${deal.address}<br>`;
            hintContent += 'Монт. бригада: ';
            hintContent += deal.installation_user ? deal.installation_user.full_name : '<span style="color: red">Не назначена</span>';
            hintContent += '<br>';
            if (deal.measurer_user) {
                hintContent += `Замерщик: ${deal.measurer_user.full_name}<br>`;
            }
            if (deal.volume) {
                hintContent += `Объем: ${deal.volume}<br>`;
            }
            if (deal.garbage_removal) {
                hintContent += 'Вывоз мусора: ';
                if (garbageRemoval.includes(deal.garbage_removal_id)) {
                    hintContent += `<b>${deal.garbage_removal.name}</b>`;
                } else {
                    hintContent += `${deal.garbage_removal.name}`;
                }
            }
            return hintContent;
        },
        createBalloonTitle(deal) {
            let iconContent = `${deal.installation_user ? deal.installation_user.full_name.split(' -')[0] : ''}`;
            if (deal.finish) {
                iconContent += '<b> #Отд</b>';
            }
            if (deal.garbage_removal_id && garbageRemoval.includes(deal.garbage_removal_id)) {
                iconContent += '<b> #Мус</b>';
            }
            return iconContent;
        },
        createBalloonContentHeader(deal) {
            let balloonContentTags = '';
            let balloonContentHeader = this.createBalloonHint(deal);
            if (deal.finish) {
                balloonContentTags += '<span class="label label-danger">Отделка</span>';
            }
            if (deal.garbage_removal_id && garbageRemoval.includes(deal.garbage_removal_id)) {
                balloonContentTags += `<span class="label label-danger">${deal.garbage_removal.name}</span>`;
            }
            balloonContentHeader += `<div>${balloonContentTags}</div>`;
            return balloonContentHeader;
        },
        createBalloonContentBody(deal) {
            const installers = this.prepareInstallerListByDeal(deal);
            return installers.map(installer =>
                `<label class="employer-selector ${installer.disabled ? 'employer-selector--disabled' : ''}">
                    <input class="employer-radio"
                        type="radio"
                        name="deal-${deal.id}-employers"
                        data-deal-id="${deal.id}"
                        data-installer-id="${installer.id}"
                        value="${installer.id}"
                        ${installer.assigned ? 'checked' : ''}
                        ${installer.disabled ? 'disabled' : ''}
                    >
                    <span class="employer-select"></span>
                    <span style="color: ${installer.labelColor}">
                        ${installer.full_name}${installer.disabled ? ' (' + installer.disabledComment + ')' : ''}
                    </span>
                </label>`
            ).join('');
        },
        createBalloonContentFooter(deal) {
            return `<a href="${this.$router.resolve({ name: 'deal-card', params: { id: deal.id } }).href}">
                                    Изменить в карточке сделки ${deal.id}
                        </a>`;
        },
        createMapMark(deal) {
            const MapBalloonContentLayout = ymaps.templateLayoutFactory.createClass(
                '<div id="balloon-{{properties.dealId}}" class="balloon-content">' +
                    '<div class="balloon-content__header">$[properties.balloonContentHeader]</div>' +
                    '<div class="balloon-content__body">$[properties.balloonContentBody]</div>' +
                    '<div class="balloon-content__footer">$[properties.balloonContentFooter]</div>' +
                '</div>'
            );
            const coords = deal.coordinates ? deal.coordinates.split(' ').map(coord => +coord) : localReferences.mapCenterCoordinates;
            const mapMark = new ymaps.Placemark(
                coords,
                {
                    iconContent: this.createBalloonTitle(deal),
                    hintContent: this.createBalloonHint(deal),
                    balloonContentHeader: this.createBalloonContentHeader(deal),
                    balloonContentBody: '',
                    balloonContentFooter: this.createBalloonContentFooter(deal),
                    dealId: deal.id
                }, {
                    balloonContentLayout: MapBalloonContentLayout,
                    preset: deal.installation_date === deal.delivery_date ? 'islands#redStretchyIcon' : 'islands#grayStretchyIcon',
                    draggable: true,
                    balloonContentSize: [458, 320],
                    balloonLayout: 'default#imageWithContent',
                    balloonImageHref: '/assets/map-balloon-1px.png',
                    balloonImageOffset: [-229, -380],
                    balloonImageSize: [458, 380],
                    balloonShadow: false
                }
            );
            mapMark.events.add('balloonopen', () => {
                const changeInstaller = (event) => {
                    this.changeInstaller(+event.target.dataset.dealId, +event.target.dataset.installerId);
                };
                this.mapObject.panTo(mapMark.geometry.getCoordinates(), { useMapMargin: true });
                mapMark.properties.set('balloonContentBody', this.createBalloonContentBody(deal));
                let balloonContentElement = document.getElementById(`balloon-${deal.id}`);
                if (balloonContentElement === null) {
                    //Если вдруг при открытии первого балуна его содержимое доступно не сразу
                    setTimeout(() => {
                        balloonContentElement = document.getElementById(`balloon-${deal.id}`);
                        Array.prototype.forEach.call(balloonContentElement.getElementsByClassName('employer-radio'), (radioElement) => {
                            radioElement.addEventListener('change', changeInstaller);
                        });
                    }, 500);
                } else {
                    Array.prototype.forEach.call(balloonContentElement.getElementsByClassName('employer-radio'), (radioElement) => {
                        radioElement.addEventListener('change', changeInstaller);
                    });
                }
            });
            return mapMark;
        },
        updateCollection(dealId) {
            let changedMark = null;
            this.collection.each(mark => {
                if (mark.properties.get('dealId') === dealId) {
                    changedMark = mark;
                }
            });
            if (changedMark) {
                this.collection.remove(changedMark);
                this.collection.add(this.createMapMark(this.deals.find(deal => deal.id === dealId)));
            }
        },
        checkInstallerAvailability(installer, deal) {
            if (installer.timesheets && installer.timesheets.length) {
                const timesheet = installer.timesheets.find(timesheet => timesheet.day === deal.installation_date.split('.').reverse().join('-'));
                if (timesheet && timesheet.status_id !== localReferences.timesheet.statuses.working) {
                    return { disabled: true, disabledComment: `${timesheet.status.name}` };
                }
            }
            if (!this.isCanEdit) {
                return { disabled: true, disabledComment: 'нет прав на изменение' };
            }
            return { disabled: false, disabledComment: '' };
        },
        checkInstallerOccupance(installer, deal) {
            let color = 'darkgreen';
            if (installer.installer_deals && installer.installer_deals.length) {
                const DealsAtSameDay = installer.installer_deals.filter(installerDeal => installerDeal.installation_date === deal.installation_date);
                switch (DealsAtSameDay.length) {
                    case 0:
                        color = 'darkgreen';
                        break;
                    case 1:
                        color = 'navy';
                        break;
                    case 2:
                        color = 'orange';
                        break;
                    default:
                        color = 'red';
                }
            }
            return color;
        },
        prepareInstallerListByDeal(deal) {
            return this.installers.map(installer => {
                const availability = this.checkInstallerAvailability(installer, deal);
                const orderDigits = installer.full_name.match(/\d+/g);
                return {
                    ...installer,
                    assigned: installer.id === deal.installation_user_id,
                    disabled: availability.disabled,
                    disabledComment: availability.disabledComment,
                    order: orderDigits && orderDigits[0] ? +orderDigits[0] : undefined,
                    labelColor: this.checkInstallerOccupance(installer, deal)
                };
            }).sort((a, b) => a.order - b.order);
        },
        changeDate() {
            Object.keys(this.offices).forEach(id => { this.offices[id] = false; });
            this.selectOffice(false);
            this.getData();
        },
        handleInformButtonClick() {
            this.$confirm('Отправить информацию по монтажам?', 'Внимание', {
                confirmButtonText: 'Да',
                cancelButtonText: 'Отмена',
                type: '',
                center: true
            }).then(() => {
                this.informInstallers();
            }).catch(() => {
            });
        },
        handleUpdateSettingsButtonClick() {
            this.updateInstallLimitPerDay();
        },
        selectOffice(updateData = true) {
            this.officeIds = Object.keys(this.offices).filter(id => this.offices[id]).join(',');
            if (updateData) {
                this.getData();
            }
        },
        async changeInstaller(dealId, installerId) {
            try {
                this.loading = true;
                await this.$store.dispatch('map/changeInstaller', {
                    dealId, installerId
                });
                this.updateCollection(dealId);
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
        async getData() {
            console.log('getData');
            try {
                this.loading = true;
                await this.$store.dispatch('map/getInstallations', {
                    day: this.selectedDay,
                    officeIds: this.officeIds
                });
                if (this.isMapReady) {
                    this.placeMarks();
                }
                this.deliveryLimitPerDay = this.$store.state.map.dailyPlan.delivery;
                this.installLimitPerDay = this.$store.state.map.dailyPlan.installation;
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
        async informInstallers() {
            try {
                this.loading = true;
                const response = await this.$store.dispatch('map/informInstallers', {
                    dealIds: this.deals.filter(deal => deal.installation_user_id !== null).map(deal => deal.id)
                });
                let message = '<b>Монтажники не назначены</b>';
                let type = 'warning';
                let title = 'Внимание';
                const deals = Object.values(response.report);
                if (deals.length) {
                    message = deals.map(deal => `Сделка ${deal.id}, ${deal.installer}<br>${deal.letter}<br>${deal.sms}`).join('<hr>');
                    type = 'success';
                    title = 'Успешно';
                }
                this.$notify({
                    duration: 10000,
                    title,
                    dangerouslyUseHTMLString: true,
                    message,
                    type
                });
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
        async updateInstallLimitPerDay() {
            try {
                this.loading = true;
                await this.$store.dispatch('map/updateInstallLimitPerDay', {
                    installationLimit: +this.installLimitPerDay,
                    deliveryLimit: +this.deliveryLimitPerDay,
                    date: this.selectedDay
                }).then(response => {
                    this.$message({
                        showClose: true,
                        message: 'Лимиты на доставку/монтаж обновлены',
                        type: 'success',
                        duration: 5000,
                        customClass: this.alertClass()
                    });
                });
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        }
    },
    mounted() {
        this.getData();
        this.getMapScript();
    }
};
</script>
